<template>
  <q-dialog persistent ref="dialog" v-model="showDialog" @hide="onDialogHide" class="amex-card-dialog">
    <q-card flat bordered class="amex-card">
      <q-card-section class="row items-center q-pb-sm">
        <div class="text-h6">American Express</div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup @click="onCancel" />
      </q-card-section>

      <q-separator inset />

      <q-card-section>
        <!-- 3DS challenge iframe is injected here by three-ds.min.js -->
        <div id="3DSUI"></div>

        <div class="text-subtitle2">Order {{ orderId }}</div>
        <div class="row full-width justify-between items-center">
          <div class="text-subtitle1 text-primary">Total</div>
          <div class="text-subtitle1 text-primary">฿ {{ amount | formattedPrice }}</div>
        </div>
      </q-card-section>

      <q-separator inset />

      <q-card-section v-if="errorMessage" class="q-pb-none">
        <div class="text-subtitle2 text-negative">{{ errorMessage }}</div>
      </q-card-section>

      <q-card-section v-if="initializing">
        <div class="row justify-center">
          <q-spinner size="lg" color="primary" />
        </div>
        <div class="row justify-center q-mt-sm">
          <div class="text-subtitle2 no-bold text-grey-8">Preparing secure card form...</div>
        </div>
      </q-card-section>

      <!-- Inputs are readonly placeholders; session.js replaces them with hosted iframes -->
      <q-card-section v-show="!initializing">
        <div class="text-h5 q-mb-md">Card details</div>

        <div class="card-field">
          <div class="text-subtitle2">Cardholder name</div>
          <input type="text" id="cardholder-name" class="card-input" title="cardholder name"
            aria-label="enter name on card" value="" tabindex="1" readonly>
        </div>

        <div class="card-field">
          <div class="text-subtitle2">Card number</div>
          <input type="text" id="card-number" class="card-input" title="card number"
            aria-label="enter your card number" value="" tabindex="2" readonly>
        </div>

        <div class="row q-col-gutter-sm">
          <div class="col">
            <div class="text-subtitle2">Month</div>
            <input type="text" id="expiry-month" class="card-input" title="expiry month"
              aria-label="two digit expiry month" value="" placeholder="MM" tabindex="3" readonly>
          </div>
          <div class="col">
            <div class="text-subtitle2">Year</div>
            <input type="text" id="expiry-year" class="card-input" title="expiry year"
              aria-label="two digit expiry year" value="" placeholder="YY" tabindex="4" readonly>
          </div>
          <div class="col">
            <div class="text-subtitle2">CVV</div>
            <input type="text" id="security-code" class="card-input" title="security code"
              aria-label="four digit CID security code" value="" placeholder="1234" tabindex="5" readonly>
          </div>
        </div>
      </q-card-section>

      <q-separator inset />

      <q-card-section>
        <q-btn unelevated no-caps no-wrap class="full-width" block color="primary" label="Pay now"
          :loading="paying" :disable="initializing || !sessionId" @click="onPayNow">
          <template v-slot:loading>
            <q-spinner class="on-left" />
            Processing
          </template>
        </q-btn>
        <q-btn flat no-caps class="full-width q-mt-sm" color="grey-8" label="Cancel"
          v-close-popup @click="onCancel" />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { QDialog, QCard, QCardSection, QSeparator, QSpace, QBtn, QSpinner } from 'quasar';
import { MASTERCARD_MERCHANT_ID } from '@/utils/domain';

const SESSION_UPDATE_TIMEOUT_MS = 10000;

export default {
  name: 'AmexCardPaymentDialog',
  components: {
    QDialog,
    QCard,
    QCardSection,
    QSeparator,
    QSpace,
    QBtn,
    QSpinner
  },
  props: {
    orderId: {
      type: String,
      required: true
    },
    amount: {
      type: Number,
      required: true
    },
    currency: {
      type: String,
      default: 'THB'
    },
    saleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showDialog: false,
      initializing: false,
      paying: false,
      errorMessage: null,
      token: null,
      sessionId: null,
      paymentId: null,
      transactionId: null,
      formSessionUpdateResolver: null
    };
  },
  watch: {
    showDialog(newVal) {
      if (newVal) {
        // wait for the dialog DOM so session.js can find the input ids
        this.$nextTick(() => this.initializePayment());
      }
    }
  },
  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      this.$refs.dialog.hide();
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async initializePayment() {
      this.initializing = true;
      this.errorMessage = null;
      try {
        // the page kicks this off when Amex is selected; awaiting it resolves the same
        // in-flight load instead of racing the gateway scripts
        await this.$services.pay.configureMastercardCardScripts();

        if (!window.PaymentSession || !window.ThreeDS) {
          throw new Error('Payment scripts are not loaded. Please reselect the payment method.');
        }

        const session = await this.$services.authentication.getApikey();
        this.token = session.key;

        const redirectUrl = new URL('/thankyou', window.location.origin);
        redirectUrl.searchParams.set('orderId', this.orderId);
        redirectUrl.searchParams.set('amount', this.amount.toString());

        const response = await this.$services.pay.initCardPayment({
          orderId: this.orderId,
          amount: this.amount,
          currency: this.currency,
          redirectResponseUrl: redirectUrl.toString(),
          saleId: this.saleId
        }, this.token);

        console.log('Card payment session initialized:', response);
        this.sessionId = response.sessionId;
        this.paymentId = response.paymentId;
        this.transactionId = response.transactionId;

        window.PaymentSession.configure({
          session: response.sessionId,
          fields: {
            card: {
              number: '#card-number',
              securityCode: '#security-code',
              expiryMonth: '#expiry-month',
              expiryYear: '#expiry-year',
              nameOnCard: '#cardholder-name'
            }
          },
          frameEmbeddingMitigation: ['javascript'],
          callbacks: {
            initialized: (res) => {
              console.log('PaymentSession initialized', res);
              this.initializing = false;
            },
            formSessionUpdate: (res) => {
              console.log('formSessionUpdate', res);
              if (this.formSessionUpdateResolver) {
                this.formSessionUpdateResolver(res.status === 'ok' ? res : null);
                this.formSessionUpdateResolver = null;
              }
            }
          }
        });
      } catch (error) {
        console.error('Failed to initialize card payment:', error);
        this.initializing = false;
        this.resetSession();
        this.showError(error.response?.status === 401
          ? 'Authentication failed. Please try logging in again'
          : (error.message || 'Failed to initialize payment'));
      }
    },

    updateSessionFromFormAndWait() {
      return new Promise((resolve, reject) => {
        this.formSessionUpdateResolver = resolve;
        window.PaymentSession.updateSessionFromForm('card');

        setTimeout(() => {
          if (this.formSessionUpdateResolver) {
            this.formSessionUpdateResolver = null;
            reject(new Error('Session update timeout'));
          }
        }, SESSION_UPDATE_TIMEOUT_MS);
      });
    },

    // Step 1: Configure ThreeDS
    configure3DS() {
      window.ThreeDS.configure({
        merchantId: MASTERCARD_MERCHANT_ID,
        sessionId: this.sessionId,
        containerId: '3DSUI',
        callback: () => console.log('ThreeDS configured'),
        configuration: {
          userLanguage: 'en',
          wsVersion: 100
        }
      });
    },

    // Step 2: Initiate 3DS authentication check
    check3DSAuthentication() {
      return new Promise((resolve, reject) => {
        const params = { sourceOfFunds: { type: 'CARD' } };

        window.ThreeDS.initiateAuthentication(this.orderId, this.transactionId, (data) => {
          if (data?.error) {
            reject(data.error);
            return;
          }

          console.log('3DS check result:', data.gatewayRecommendation);

          if (data.gatewayRecommendation === 'PROCEED') {
            resolve(data);
          } else if (data.gatewayRecommendation === 'DO_NOT_PROCEED') {
            reject(new Error('Authentication failed. Please check your card details and try again'));
          } else if (data.gatewayRecommendation === 'RESUBMIT_WITH_ALTERNATIVE_PAYMENT_DETAILS') {
            reject(new Error('This card does not support 3D Secure. Please use another card'));
          } else {
            reject(new Error('Unable to proceed. Please try again'));
          }
        }, params);
      });
    },

    // Step 3: Show 3DS challenge and process payment
    authenticate3DSAndPay() {
      return new Promise((resolve, reject) => {
        const params = { fullScreenRedirect: false };

        window.ThreeDS.authenticatePayer(this.orderId, this.transactionId, async (data) => {
          if (data?.error) {
            reject(data.error);
            return;
          }

          console.log('3DS challenge completed:', data.gatewayRecommendation);

          if (data.gatewayRecommendation !== 'PROCEED') {
            reject(new Error('Authentication not approved. Cannot proceed with payment'));
            return;
          }

          try {
            const response = await this.$services.pay.payCardPayment({
              paymentId: this.paymentId,
              isSaveCard: false
            }, this.token);

            console.log('Card payment response:', response);
            resolve(response);
          } catch (error) {
            reject(error);
          }
        }, params);
      });
    },

    async onPayNow() {
      try {
        this.paying = true;

        const updateResult = await this.updateSessionFromFormAndWait();
        if (!updateResult) {
          throw new Error('Failed to read card data. Please check and try again');
        }

        this.configure3DS();
        await this.check3DSAuthentication();
        const response = await this.authenticate3DSAndPay();

        this.paying = false;
        this.$emit('payment-success', response);

        window.location.href = response?.redirectUrl
          || `/thankyou?orderId=${this.orderId}&status=success&amount=${this.amount}`;
      } catch (error) {
        console.error('Card payment error:', error);
        this.paying = false;

        // the gateway session is single-use once 3DS has been attempted: start over
        this.resetSession();
        this.showError(error.message || 'Payment failed');
        this.initializePayment();
      }
    },

    resetSession() {
      this.sessionId = null;
      this.paymentId = null;
      this.transactionId = null;
      this.formSessionUpdateResolver = null;
    },

    showError(message) {
      console.error('Card payment error:', message);
      this.errorMessage = message;
    },

    onCancel() {
      this.resetSession();
      this.$emit('payment-cancelled');

      // PaymentSession/ThreeDS bind their iframes to this dialog's DOM and cannot be
      // re-configured once it is torn down, so reload the page to get a clean state
      window.location.reload();
    }
  }
};
</script>

<style scoped>

.amex-card {
  width: 100%;
  min-width: 360px;
  max-width: 460px;
}

.no-bold {
  font-weight: normal;
}

.card-field {
  margin-bottom: 16px;
}

/* mimics q-field--outlined so the hosted iframes blend with the page's Quasar inputs */
.card-input {
  width: 100%;
  height: 40px;
  margin-top: 4px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card-input:hover {
  border-color: rgba(0, 0, 0, 0.6);
}

.card-input:focus {
  outline: none;
  border-color: var(--q-color-primary, #2c2a74);
  box-shadow: inset 0 0 0 1px var(--q-color-primary, #2c2a74);
}

.card-input::placeholder {
  color: rgba(0, 0, 0, 0.4);
}

@media (max-width: 485px) {
  .amex-card {
    min-width: 0;
  }
}

</style>
